import React, { useState } from "react";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";
import { API_BASE_URL } from "../../config/api";

function AccountDeletion() {
  const API_URL = `${API_BASE_URL}/auth/account`;

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    setIsLoading(true);

    try {
      const response = await fetch(API_URL, {
        method: "DELETE", // or DELETE — depends on your API
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setMessage(
          "Your account deletion request has been submitted successfully."
        );
        setFormData({ email: "", password: "" });
      } else {
        setError(result.message || "Failed to process deletion request.");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-800 px-6 py-10 max-w-md mx-auto">
      <h1 className="text-3xl font-bold text-red-600">Delete Account</h1>

      <p className="text-gray-500 text-sm mt-2">
        Request permanent deletion of your account and associated data.
      </p>

      {/* Success */}
      {message && (
        <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-xl text-sm">
          {message}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-xl flex items-start text-sm">
          <AlertTriangle className="w-5 h-5 mr-2 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <form className="mt-8 space-y-6" onSubmit={handleDelete}>
        <div>
          <label className="text-gray-600 text-sm">Email Address</label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="name@email.com"
            className="mt-1 w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-red-500 outline-none"
          />
        </div>

        <div>
          <label className="text-gray-600 text-sm">Password</label>
          <input
            type="password"
            name="password"
            required
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter your password"
            className="mt-1 w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-red-500 outline-none"
          />
        </div>

        <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-4 text-sm text-yellow-800">
          <strong>Warning:</strong> This action is permanent and cannot be
          undone. All your data will be deleted.
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className={`w-full py-3 rounded-xl font-semibold text-white transition ${
            isLoading
              ? "bg-red-300 cursor-not-allowed"
              : "bg-red-600 hover:bg-red-700"
          }`}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 inline mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Trash2 className="w-5 h-5 inline mr-2" />
              Delete My Account
            </>
          )}
        </button>
      </form>

      {/* Compliance text */}
      <p className="text-xs text-gray-500 mt-6">
        After submitting, your account and associated personal data will be
        deleted in accordance with our data retention policy.
      </p>
    </div>
  );
}

export default AccountDeletion;
